import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/ui/button";
import { useDiagnosticCompanies } from "../../hooks/useDiagnosticCompanies";
import { NewDiagnosticDialog } from "../NewDiagnosticDialog/NewDiagnosticDialog";
import { DiagnosticLoadFailed } from "../DiagnosticLoadFailed";
import { DiagnosticCompaniesSkeleton } from "../DiagnosticSkeletons";
import type { DiagnosticCompanyRow } from "../../types/diagnostic";

/**
 * Directory of diagnosed companies. A diagnostic assesses a business, so the
 * tenant is the row — there is no per-person view.
 *
 * Rows are opened through a real `<Link>` in the company cell, not a click
 * handler on the `<tr>` and not a `<button>` calling `navigate()` (D23). The
 * `<tr>` is the load-bearing part: it is not a button, and making it act like
 * one costs `role="button"`, which strips the row out of the table's own
 * semantics and orphans its cells. The cell is where a destination belongs.
 *
 * The `<button>` this replaced was already keyboard-operable, so this is not
 * the rescue the QA report described — but a button is not a link. Middle-
 * click, cmd-click, "open in new tab" and "copy link address" were all inert,
 * and a screen reader announced a button with no destination.
 */
export const CompaniesList: React.FC = () => {
  const {
    companies,
    totalCount,
    hasLoaded,
    status,
    loading,
    pagination,
    searchTerm,
    handlePageChange,
    handleSearchChange,
    refetch,
  } = useDiagnosticCompanies();
  const [newOpen, setNewOpen] = useState(false);

  const columns: Column<DiagnosticCompanyRow>[] = [
    {
      id: "name",
      header: "Company",
      cell: ({ row }) => (
        <Link
          className="text-left text-gray-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:text-white dark:focus-visible:ring-slate-400"
          to={`/admin/diagnostics/${row.original.companyId}`}
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: "diagnosticName",
      header: "Latest diagnostic",
      cell: ({ row }) => (
        <span className="text-gray-500 dark:text-gray-400">
          {row.original.latestAttempt?.diagnosticName ?? "—"}
        </span>
      ),
    },
    {
      id: "stage",
      header: "Stage",
      cell: ({ row }) =>
        row.original.latestAttempt?.stage ? (
          <Badge variant="grey">{row.original.latestAttempt.stage}</Badge>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      id: "status",
      header: "Status",
      // A company with no latest attempt has no status to report. Falling
      // through to "In progress" asserted a diagnostic was under way when
      // there was none — every other column shows an em dash for the same row.
      cell: ({ row }) =>
        row.original.latestAttempt ? (
          <Badge variant="grey">
            {row.original.latestAttempt.status === "complete"
              ? "Complete"
              : "In progress"}
          </Badge>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      id: "totalAttempts",
      header: "Diagnostics",
      cell: ({ row }) => (
        <span className="tabular-nums text-gray-500 dark:text-gray-400">
          {row.original.totalAttempts}
        </span>
      ),
    },
  ];

  // In flight, with nothing ever loaded. The skeleton stands in for a page
  // that has not been drawn yet, so the only thing that may put it back is
  // never having drawn one: the old guard also fired on `companies.length ===
  // 0 && searchTerm === ""`, which a search matching nothing and then being
  // cleared satisfies in full — replacing the live page, search box included,
  // with its first-paint skeleton mid-session.
  if (status === "loading" && !hasLoaded) {
    return <DiagnosticCompaniesSkeleton />;
  }

  return (
    <MainPageWrapper
      actions={
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          New Diagnostic
        </Button>
      }
      title="Diagnostics"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          Companies with at least one diagnostic on record. Open a company for
          its full history.
        </p>
        <SearchInput
          className="w-72"
          onChange={handleSearchChange}
          placeholder="Search companies…"
          value={searchTerm}
        />
      </div>

      <div className="mt-6">
        {/* A failed load is not an empty directory. Absorbing the rejection
            left `companies: []` with nothing to distinguish it from a
            successful load of nothing, so the table announced "No diagnostics
            yet" over however many the system holds — the one reading in this
            feature that could send a coach away believing there was nothing
            here. The failure replaces the table rather than the page so the
            search box and New Diagnostic stay usable. */}
        {status === "failed" ? (
          <DiagnosticLoadFailed
            message="The companies list could not be loaded."
            onRetry={refetch}
          />
        ) : (
          <DataTable<DiagnosticCompanyRow>
            columns={columns}
            data={companies}
            loading={loading}
            noDataMessage={
              searchTerm
                ? "No companies match that search."
                : "No diagnostics yet. Use New Diagnostic to start one."
            }
            onPageChange={handlePageChange}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            rowKeyExtractor={(company) => company.companyId}
            totalCount={totalCount}
          />
        )}
      </div>

      <NewDiagnosticDialog onClose={() => setNewOpen(false)} open={newOpen} />
    </MainPageWrapper>
  );
};

export default CompaniesList;
